import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/context/BrandingContext";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { api, apiError, logError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Login() {
  const { login, reload, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [adminName, setAdminName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  useEffect(() => {
    api
      .get("/auth/bootstrap")
      .then(({ data }) => setMode(data.needs_setup ? "bootstrap" : "signin"))
      .catch((e) => logError("bootstrap status", e));
  }, []);

  const sendReset = async () => {
    setForgotBusy(true);
    setForgotMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      setForgotMsg(data.message);
    } catch (e) {
      setForgotMsg(apiError(e));
    } finally {
      setForgotBusy(false);
    }
  };

  const submitSignIn = async () => {
    const res = await login(email.trim(), password);
    if (res.ok) return;
    if (res.status === 409) {
      setMode("create");
      setError("");
      return;
    }
    setError(res.error);
  };

  const submitCreate = async () => {
    if (newPassword !== confirmPassword) {
      setError("Both passwords must match");
      return;
    }
    try {
      await api.post("/auth/setup-password", { email: email.trim(), new_password: newPassword });
      await reload();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const submitBootstrap = async () => {
    if (newPassword !== confirmPassword) {
      setError("Both passwords must match");
      return;
    }
    try {
      await api.post("/auth/bootstrap", {
        name: adminName.trim(), email: email.trim(), password: newPassword,
      });
      await reload();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    if (mode === "signin") await submitSignIn();
    else if (mode === "create") await submitCreate();
    else await submitBootstrap();
    setBusy(false);
  };

  const copy = {
    signin: {
      overline: "Secure sign in",
      title: "Welcome back",
      hint: "Sign in to the Pramsia business operating system.",
      cta: "Sign in",
    },
    create: {
      overline: "First time sign in",
      title: "Create your password",
      hint: "No password is set for this account yet. Choose your own — only you will know it.",
      cta: "Create password & continue",
    },
    bootstrap: {
      overline: "Installation setup",
      title: "Create your admin account",
      hint: "This is a fresh installation. Create the owner account that will control everything.",
      cta: "Create admin account",
    },
  }[mode];

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-slate-950 lg:block">
        <img
          src="https://images.unsplash.com/photo-1564182842834-681b7be6de4b?auto=format&fit=crop&w=1400&q=70"
          alt="Pramsia manufacturing floor"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-slate-100">
          <BrandMark className="h-14 w-14" dark />
          <div className="max-w-lg">
            <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-300">
              Digital Brain • ERP • CRM • Manufacturing
            </div>
            <h2 className="font-display text-4xl font-bold leading-tight">
              One system for quotations, production, stock, projects and money.
            </h2>
            <p className="mt-4 text-sm text-slate-300">
              Water slides, water park equipment, playground and customised FRP manufacturing —
              from enquiry to installation to final payment.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 border-t border-slate-800 pt-6 text-xs text-slate-400">
              <div>
                <div className="font-mono-num text-xl text-slate-100">GST</div>
                Ready billing
              </div>
              <div>
                <div className="font-mono-num text-xl text-slate-100">AI</div>
                Employee ops
              </div>
              <div>
                <div className="font-mono-num text-xl text-slate-100">BOM</div>
                Manufacturing
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="fixed right-4 top-4 z-20">
          <ThemeSwitcher compact />
        </div>
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 lg:hidden">
            <BrandMark className="h-11 w-11" />
          </div>
          <div className="overline">{copy.overline}</div>
          <h1 className="mt-1 font-display text-3xl font-bold">{copy.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{copy.hint}</p>

          <div className="mt-8 space-y-4">
            {mode === "bootstrap" && (
              <div>
                <Label htmlFor="adminName">Your name</Label>
                <Input
                  id="adminName"
                  data-testid="bootstrap-name-input"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  required
                  className="mt-1.5"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                readOnly={mode === "create"}
                className="mt-1.5"
              />
            </div>
            {mode === "signin" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => {
                      setForgotEmail(email);
                      setForgotOpen(true);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="mt-1.5"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  First time here? Enter your email and press Sign in — you will be asked to create
                  your own password.
                </p>
              </div>
            )}
            {mode !== "signin" && (
              <>
                <div>
                  <Label htmlFor="newPassword">New password</Label>
                  <Input
                    id="newPassword"
                    data-testid="new-password-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    className="mt-1.5"
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    At least 10 characters with letters and numbers.
                  </p>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm password</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    className="mt-1.5"
                  />
                </div>
              </>
            )}
            {error && (
              <div
                data-testid="login-error"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {copy.cta}
            </Button>

            {mode !== "bootstrap" && (
              <>
                <div className="flex items-center gap-3 py-1">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">or</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  data-testid="google-signin-button"
                  onClick={loginWithGoogle}
                >
                  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
                    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v8.3h12.9c-.6 3.1-2.5 5.5-5.2 7.1l7.7 6c4.5-4.2 7.1-10.4 7.1-17.3z" />
                    <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.8-6.1z" />
                    <path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.4-5.6l-7.7-6c-2.1 1.4-4.8 2.3-7.7 2.3-6.3 0-11.7-3.7-13.6-9.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
                  </svg>
                  Continue with Google
                </Button>
                <p className="text-center text-[11px] text-muted-foreground" data-testid="google-signin-note">
                  Use the Google account registered with your Pramsia employee record.
                </p>
              </>
            )}
            {mode === "create" && (
              <button
                type="button"
                data-testid="back-to-signin-button"
                onClick={() => {
                  setMode("signin");
                  setError("");
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground"
              >
                Back to sign in
              </button>
            )}
          </div>

          <div className="mt-8 flex items-start gap-2 rounded-md border border-border bg-secondary/60 p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            Access is role based and every action is audit logged. Contact the owner for an account.
          </div>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent data-testid="forgot-password-dialog">
              <DialogHeader>
                <DialogTitle>Reset your password</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground">
                Enter your account email. A one-time reset link (valid 60 minutes) is generated. Email
                delivery needs an email provider configured — until then, ask the owner to hand over the
                link from the server, or to issue a temporary password from Settings → Employees.
              </p>
              <Label htmlFor="fe" className="mt-2">
                Email
              </Label>
              <Input
                id="fe"
                data-testid="forgot-email-input"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
              {forgotMsg && (
                <div
                  data-testid="forgot-password-message"
                  className="rounded-md border border-border bg-secondary/60 px-3 py-2 text-xs"
                >
                  {forgotMsg}
                </div>
              )}
              <DialogFooter>
                <Button data-testid="forgot-submit-button" disabled={forgotBusy} onClick={sendReset} className="gap-2">
                  {forgotBusy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </form>
      </div>
    </div>
  );
}
